import {
  ArrowRightIcon,
  CheckCircleIcon,
  CloudIcon,
  CodeIcon,
  DatabaseIcon,
  GlobeAltIcon,
  LockClosedIcon,
  RefreshIcon,
} from '@heroicons/react/outline';
import Image from 'next/image';
import Link from 'next/link';
import useTranslation from 'next-translate/useTranslation';

import Footer from '@/components/layout/Footer';
import LandingPageNavigation from '@/components/layout/LandingPageNavigation';
import Seo from '@/components/Seo';

import {
  MARKY_WEBSITE_FAQS,
  markyWebsiteUseCases,
} from '@/constants/markyWebsiteUseCases';
import {
  translateMarkyWebsiteFaqs,
  translateMarkyWebsiteText,
  translateMarkyWebsiteUseCase,
} from '@/utils/markyWebsiteTranslation';

import MarkyWebsiteJsonLd from './MarkyWebsiteJsonLd';

const SITE_URL = 'https://easy-peasy.ai';

const capabilityRows = [
  {
    capability: 'Frontend',
    simple: 'Vite, React, TypeScript and Tailwind CSS',
    fullStack: 'Vite, React, TypeScript and Tailwind CSS',
  },
  {
    capability: 'Server',
    simple: 'Not included',
    fullStack: 'Express with typed tRPC APIs',
  },
  {
    capability: 'Database',
    simple: 'Not included',
    fullStack: 'Managed Neon Postgres with Drizzle',
  },
  {
    capability: 'User accounts',
    simple: 'Not included',
    fullStack: 'Better Auth foundation and Google OAuth support',
  },
  {
    capability: 'File uploads',
    simple: 'Static project assets',
    fullStack: 'Cloudflare R2, using an S3-compatible API',
  },
  {
    capability: 'Best for',
    simple: 'Marketing sites, portfolios and information pages',
    fullStack: 'Portals, SaaS, directories and operational apps',
  },
];

const infrastructure = [
  {
    label: 'Preview',
    value: 'A public sandbox URL that updates while Marky works',
    Icon: RefreshIcon,
  },
  {
    label: 'Production',
    value: 'Google Cloud Run after you explicitly approve publishing',
    Icon: CloudIcon,
  },
  {
    label: 'Data',
    value: 'A managed Neon Postgres branch for each full-stack project',
    Icon: DatabaseIcon,
  },
  {
    label: 'Authentication',
    value: 'Better Auth foundation with centralized Google OAuth support',
    Icon: LockClosedIcon,
  },
  {
    label: 'Domains',
    value: 'An editable subdomain, a connected domain or a purchased domain',
    Icon: GlobeAltIcon,
  },
  {
    label: 'Source code',
    value: 'Create or connect a GitHub repository and push project changes',
    Icon: CodeIcon,
  },
];

const buildSteps = [
  {
    number: '01',
    title: 'Describe the outcome',
    description:
      'Give Marky the users, core workflow, records and visual direction—not just a page count.',
  },
  {
    number: '02',
    title: 'Review the live preview',
    description:
      'Marky scaffolds the project, runs it and opens a URL you can inspect while the conversation continues.',
  },
  {
    number: '03',
    title: 'Refine and checkpoint',
    description:
      'Request changes in plain language and save a named working version before larger revisions.',
  },
  {
    number: '04',
    title: 'Publish when it is ready',
    description:
      'Approve production deployment, choose the URL and optionally connect the codebase to GitHub.',
  },
];

const capabilityGroups = [
  {
    title: 'Build and inspect',
    summary: 'The working code and feedback loop',
    items: [
      'Vite, React, TypeScript and Tailwind CSS for every web project',
      'Express and typed tRPC APIs when the project needs a backend',
      'Live preview, project health checks, commands and tests for full-stack work',
      'An upgrade path from a simple website to the full-stack scaffold',
    ],
  },
  {
    title: 'Store and protect',
    summary: 'Persistent application data and identity',
    items: [
      'A managed Neon Postgres branch provisioned for the project',
      'Drizzle models, schema changes, migrations and direct SQL operations',
      'Better Auth foundations and centralized Google OAuth support',
      'Cloudflare R2 object storage through its S3-compatible interface',
    ],
  },
  {
    title: 'Connect services',
    summary: 'Protected credentials and external capabilities',
    items: [
      'Project secrets for private API keys and service credentials',
      'Easy-Peasy.AI endpoints for text, chat, images, video, speech, transcription, bots and sounds',
      'Third-party APIs for payments, email, calendars, maps and similar workflows',
      'AWS APIs and services when configured—separate from the default Cloud Run publish path',
    ],
  },
  {
    title: 'Ship and recover',
    summary: 'Versions, domains and production operation',
    items: [
      'Automatic preview checkpoints, named versions and rollback controls',
      'Google Cloud Run publishing and an optional Always-On Hosting add-on',
      'Editable subdomains plus custom-domain purchase or connection, DNS and SSL setup',
      'GitHub repository creation, linking, push and later code synchronization',
    ],
  },
];

const useCaseLayout = [
  'lg:col-span-7',
  'lg:col-span-5',
  'lg:col-span-4',
  'lg:col-span-8',
  'lg:col-span-6',
  'lg:col-span-6',
];

const MarkyWebsiteHub = () => {
  const { t, lang } = useTranslation('marky-websites');
  const tr = (value: string) => translateMarkyWebsiteText(t, value);
  const localizedUseCases = markyWebsiteUseCases.map((useCase) =>
    translateMarkyWebsiteUseCase(t, useCase),
  );
  const localizedFaqs = translateMarkyWebsiteFaqs(t, MARKY_WEBSITE_FAQS);
  const isRtl = lang === 'ar';

  return (
    <main
      dir={isRtl ? 'rtl' : 'ltr'}
      className='bg-[#f7f2ea] text-[#251b2b] dark:bg-slate-950 dark:text-slate-50'
    >
      <Seo
        templateTitle={tr('Marky Full-Stack AI Website Builder')}
        description={tr(
          'Build websites and full-stack web apps with Marky using React, Postgres, Google login, file storage, GitHub, custom domains, and Google Cloud Run.',
        )}
        image={`${SITE_URL}/images/marky-websites/ai-saas-app-builder.webp`}
        page='other'
        productName={tr('Marky AI Website Builder')}
        productDescription={tr(
          'An AI website and full-stack web app builder with live preview, database, authentication, source control and managed deployment.',
        )}
        productImage={[
          `${SITE_URL}/images/marky-websites/ai-saas-app-builder.webp`,
        ]}
      />
      <MarkyWebsiteJsonLd />

      <div className='border-b border-[#34243b]/10 dark:border-white/10'>
        <LandingPageNavigation skipUserContext />
        <section className='relative overflow-hidden px-4 pb-20 pt-16 sm:px-6 sm:pb-24 sm:pt-20 lg:px-8 lg:pb-28'>
          <div
            aria-hidden='true'
            className='absolute -right-32 top-16 h-80 w-80 rounded-full border-[52px] border-[#7651c9]/10 dark:border-[#9575db]/10'
          />
          <div className='relative mx-auto grid max-w-7xl items-center gap-14 lg:grid-cols-[1.05fr_0.95fr] lg:gap-20'>
            <div>
              <p className='text-sm font-black uppercase tracking-[0.2em] text-[#6d45c2] dark:text-[#bda7ef]'>
                {tr('Marky website builder')}
              </p>
              <h1 className='mt-5 max-w-4xl text-5xl font-black tracking-[-0.055em] text-[#251b2b] dark:text-slate-50 sm:text-6xl lg:text-7xl'>
                {tr('From a chat prompt to a full-stack web app')}
              </h1>
              <p className='mt-7 max-w-2xl text-xl leading-8 text-[#5f5363] dark:text-slate-300'>
                {tr(
                  'Marky can plan, code, preview and publish a website—or build the backend, database and account flows when the idea needs real application logic.',
                )}
              </p>

              <div className='mt-8 flex flex-wrap items-center gap-2 text-sm font-bold text-[#3d3043] dark:text-slate-200'>
                {[
                  'Your brief',
                  'React + TypeScript',
                  'Postgres + auth',
                  'Cloud Run',
                ].map((item, index) => (
                  <span key={item} className='contents'>
                    <span className='rounded-full border border-[#392d3f]/15 bg-[#fffaf3] px-4 py-2 dark:border-white/10 dark:bg-slate-900'>
                      {tr(item)}
                    </span>
                    {index < 3 && (
                      <span
                        aria-hidden='true'
                        className='text-[#8b70a0] dark:text-slate-500'
                      >
                        {isRtl ? '←' : '→'}
                      </span>
                    )}
                  </span>
                ))}
              </div>

              <div className='mt-9 flex flex-col gap-3 sm:flex-row'>
                <Link
                  href='/chat?mode=build-website'
                  className='inline-flex min-h-[48px] items-center justify-center gap-2 rounded-full bg-[#34203f] px-6 py-3 font-bold text-[#fffaf3] transition-colors hover:bg-[#4d315b] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-[#6d45c2] dark:bg-primary-600 dark:hover:bg-primary-500'
                >
                  {tr('Build with Marky')}
                  <ArrowRightIcon
                    className={`h-4 w-4 ${isRtl ? 'rotate-180' : ''}`}
                    aria-hidden='true'
                  />
                </Link>
                <Link
                  href='#website-types'
                  className='inline-flex min-h-[48px] items-center justify-center rounded-full border border-[#34203f]/25 bg-[#fffaf3] px-6 py-3 font-bold text-[#34203f] transition-colors hover:border-[#34203f]/50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-[#6d45c2] dark:border-white/20 dark:bg-slate-900 dark:text-slate-100 dark:hover:border-white/40'
                >
                  {tr('Explore website types')}
                </Link>
              </div>
            </div>

            <div className='relative mx-auto w-full max-w-2xl pb-12 sm:pb-20'>
              <div className='relative ml-auto aspect-[3/2] w-[88%] overflow-hidden rounded-[2rem] border border-[#34243b]/15 bg-[#fffaf3] shadow-[0_28px_70px_rgba(45,30,50,0.17)] dark:border-white/10 dark:bg-slate-900 dark:shadow-none'>
                <Image
                  src={localizedUseCases[0].image}
                  alt={localizedUseCases[0].imageAlt}
                  fill
                  priority
                  sizes='(min-width: 1024px) 44vw, 88vw'
                  className='object-cover'
                />
              </div>
              <div className='absolute bottom-0 left-0 aspect-[3/2] w-[47%] overflow-hidden rounded-2xl border-4 border-[#f7f2ea] bg-[#fffaf3] shadow-[0_20px_55px_rgba(45,30,50,0.2)] dark:border-slate-950 dark:bg-slate-900 dark:shadow-none'>
                <Image
                  src={localizedUseCases[2].image}
                  alt={localizedUseCases[2].imageAlt}
                  fill
                  sizes='(min-width: 1024px) 22vw, 44vw'
                  className='object-cover'
                />
              </div>
            </div>
          </div>
        </section>
      </div>

      <section className='px-4 py-16 sm:px-6 lg:px-8'>
        <div
          className={`mx-auto max-w-5xl border-[#b7d94c] py-2 ${
            isRtl ? 'border-r-4 pr-6 sm:pr-9' : 'border-l-4 pl-6 sm:pl-9'
          }`}
        >
          <p className='text-sm font-black uppercase tracking-[0.18em] text-[#5c4e61] dark:text-slate-400'>
            {tr('Direct answer')}
          </p>
          <h2 className='mt-3 text-3xl font-black tracking-[-0.025em] sm:text-4xl'>
            {tr("What is Marky's AI website builder?")}
          </h2>
          <p className='mt-5 text-lg leading-8 text-[#5f5363] dark:text-slate-300'>
            {tr(
              'Marky is a conversational website and web app builder inside Easy-Peasy.AI. It can generate an editable React and TypeScript project, run a live preview, add an Express backend, Neon Postgres, authentication and file storage, then publish the approved version to Google Cloud Run. You can keep iterating in chat or sync the code to GitHub.',
            )}
          </p>
        </div>
      </section>

      <section
        id='website-types'
        aria-labelledby='website-types-title'
        className='border-y border-[#34243b]/10 bg-[#fffaf3] px-4 py-20 dark:border-white/10 dark:bg-slate-900 sm:px-6 lg:px-8 lg:py-28'
      >
        <div className='mx-auto max-w-7xl'>
          <div className='max-w-3xl'>
            <p className='text-sm font-black uppercase tracking-[0.2em] text-[#6d45c2] dark:text-[#bda7ef]'>
              {tr('Start with your workflow')}
            </p>
            <h2
              id='website-types-title'
              className='mt-4 text-4xl font-black tracking-[-0.04em] sm:text-5xl'
            >
              {tr('Six website types, six different data problems')}
            </h2>
            <p className='mt-5 text-lg leading-8 text-[#665a69] dark:text-slate-300'>
              {tr(
                'Each guide below explains the records, account flows and edge cases that matter for that product—not just a different visual template.',
              )}
            </p>
          </div>

          <div className='mt-12 grid grid-cols-1 gap-5 lg:grid-cols-12'>
            {localizedUseCases.map((useCase, index) => (
              <Link
                key={useCase.slug}
                href={`/marky/website-builder/${useCase.slug}`}
                className={`group overflow-hidden rounded-[1.75rem] border border-[#34243b]/15 bg-[#f7f2ea] transition-transform duration-300 hover:-translate-y-1 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-[#6d45c2] dark:border-white/10 dark:bg-slate-800 motion-reduce:transition-none ${useCaseLayout[index]}`}
              >
                <div className='relative aspect-[3/2] overflow-hidden border-b border-[#34243b]/10 dark:border-white/10'>
                  <Image
                    src={useCase.image}
                    alt={useCase.imageAlt}
                    fill
                    sizes='(min-width: 1024px) 55vw, 94vw'
                    className='object-cover transition-transform duration-500 group-hover:scale-[1.025] motion-reduce:transition-none'
                  />
                </div>
                <div className='flex items-start justify-between gap-6 p-6 sm:p-7'>
                  <div>
                    <h3 className='text-2xl font-black tracking-[-0.02em]'>
                      {useCase.name}
                    </h3>
                    <p className='mt-2 leading-7 text-[#665a69] dark:text-slate-300'>
                      {useCase.cardDescription}
                    </p>
                  </div>
                  <span
                    aria-hidden='true'
                    className='mt-1 shrink-0 text-2xl text-[#6d45c2] transition-transform group-hover:translate-x-1 dark:text-[#bda7ef] motion-reduce:transition-none'
                  >
                    {isRtl ? '←' : '→'}
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className='px-4 py-20 sm:px-6 lg:px-8 lg:py-28'>
        <div className='mx-auto max-w-7xl'>
          <div className='grid gap-10 lg:grid-cols-[0.7fr_1.3fr] lg:gap-16'>
            <div>
              <p className='text-sm font-black uppercase tracking-[0.2em] text-[#6d45c2] dark:text-[#bda7ef]'>
                {tr('Choose the right scaffold')}
              </p>
              <h2 className='mt-4 text-4xl font-black tracking-[-0.04em] sm:text-5xl'>
                {tr('Simple website or full-stack app?')}
              </h2>
              <p className='mt-5 text-lg leading-8 text-[#665a69] dark:text-slate-300'>
                {tr(
                  'Marky can start light and add the application layer when the project needs accounts, stored records or protected server logic.',
                )}
              </p>
            </div>

            <div className='overflow-x-auto rounded-2xl border border-[#34243b]/15 bg-[#fffaf3] dark:border-white/10 dark:bg-slate-900'>
              <table
                className={`min-w-[700px] w-full ${
                  isRtl ? 'text-right' : 'text-left'
                }`}
              >
                <caption className='sr-only'>
                  {tr(
                    'Comparison of simple website and full-stack Marky projects',
                  )}
                </caption>
                <thead className='border-b border-[#34243b]/15 bg-[#eee6dc] text-sm uppercase tracking-[0.1em] text-[#5b4e60] dark:border-white/10 dark:bg-slate-800 dark:text-slate-300'>
                  <tr>
                    <th scope='col' className='px-5 py-4 font-black'>
                      {tr('Capability')}
                    </th>
                    <th scope='col' className='px-5 py-4 font-black'>
                      {tr('Simple website')}
                    </th>
                    <th scope='col' className='px-5 py-4 font-black'>
                      {tr('Full-stack app')}
                    </th>
                  </tr>
                </thead>
                <tbody className='divide-y divide-[#34243b]/10 dark:divide-white/10'>
                  {capabilityRows.map((row) => (
                    <tr key={row.capability}>
                      <th scope='row' className='px-5 py-4 font-bold'>
                        {tr(row.capability)}
                      </th>
                      <td className='px-5 py-4 text-[#665a69] dark:text-slate-300'>
                        {tr(row.simple)}
                      </td>
                      <td className='px-5 py-4 text-[#403444] dark:text-slate-200'>
                        {tr(row.fullStack)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </section>

      <section className='bg-[#211728] px-4 py-20 text-[#fffaf3] sm:px-6 lg:px-8 lg:py-28'>
        <div className='mx-auto max-w-7xl'>
          <div className='max-w-3xl'>
            <p className='text-sm font-black uppercase tracking-[0.2em] text-[#cab3ff]'>
              {tr('Production foundation')}
            </p>
            <h2 className='mt-4 text-4xl font-black tracking-[-0.04em] sm:text-5xl'>
              {tr('The infrastructure behind the conversation')}
            </h2>
            <p className='mt-5 text-lg leading-8 text-[#d9cedd]'>
              {tr(
                'The chat is the interface. Underneath it, Marky works with a conventional codebase and managed services you can reason about.',
              )}
            </p>
          </div>

          <dl className='mt-12 grid gap-x-10 gap-y-0 border-y border-white/15 md:grid-cols-2 lg:grid-cols-3'>
            {infrastructure.map(({ label, value, Icon }) => (
              <div
                key={label}
                className='grid grid-cols-[auto_1fr] gap-4 border-b border-white/15 py-7 md:px-3'
              >
                <Icon
                  className='mt-0.5 h-6 w-6 text-[#b7d94c]'
                  aria-hidden='true'
                />
                <div>
                  <dt className='font-black text-[#fffaf3]'>{tr(label)}</dt>
                  <dd className='mt-2 leading-7 text-[#cfc3d3]'>{tr(value)}</dd>
                </div>
              </div>
            ))}
          </dl>
        </div>
      </section>

      <section className='border-b border-[#34243b]/10 bg-[#fffaf3] px-4 py-20 dark:border-white/10 dark:bg-slate-900 sm:px-6 lg:px-8 lg:py-28'>
        <div className='mx-auto max-w-7xl'>
          <div className='grid gap-10 lg:grid-cols-[0.72fr_1.28fr] lg:gap-20'>
            <div>
              <p className='text-sm font-black uppercase tracking-[0.2em] text-[#6d45c2] dark:text-[#bda7ef]'>
                {tr('Complete capability map')}
              </p>
              <h2 className='mt-4 text-4xl font-black tracking-[-0.04em] sm:text-5xl'>
                {tr('What Marky can handle across a web project')}
              </h2>
              <p className='mt-5 text-lg leading-8 text-[#665a69] dark:text-slate-300'>
                {tr(
                  'Capabilities are selected from the brief. A brochure site does not need a database; a portal does. Marky can add the deeper layers when the product actually calls for them.',
                )}
              </p>
            </div>

            <div className='grid border-t border-[#34243b]/20 dark:border-white/10 md:grid-cols-2'>
              {capabilityGroups.map((group, index) => (
                <article
                  key={group.title}
                  className={`border-b border-[#34243b]/20 py-8 dark:border-white/10 md:px-7 ${
                    index % 2 === 0 ? 'md:border-r' : ''
                  }`}
                >
                  <p className='text-sm font-bold text-[#746878] dark:text-slate-400'>
                    {tr(group.summary)}
                  </p>
                  <h3 className='mt-2 text-2xl font-black tracking-[-0.02em]'>
                    {tr(group.title)}
                  </h3>
                  <ul className='mt-6 space-y-4'>
                    {group.items.map((item) => (
                      <li
                        key={item}
                        className='flex items-start gap-3 leading-7 text-[#5e525f] dark:text-slate-300'
                      >
                        <CheckCircleIcon
                          className='mt-1 h-5 w-5 shrink-0 text-[#62851b] dark:text-[#a9c95e]'
                          aria-hidden='true'
                        />
                        <span>{tr(item)}</span>
                      </li>
                    ))}
                  </ul>
                </article>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className='px-4 py-20 sm:px-6 lg:px-8 lg:py-28'>
        <div className='mx-auto max-w-7xl'>
          <div className='grid gap-14 lg:grid-cols-[0.8fr_1.2fr] lg:gap-20'>
            <div>
              <p className='text-sm font-black uppercase tracking-[0.2em] text-[#6d45c2] dark:text-[#bda7ef]'>
                {tr('Build loop')}
              </p>
              <h2 className='mt-4 text-4xl font-black tracking-[-0.04em] sm:text-5xl'>
                {tr('A visible path from idea to production')}
              </h2>
            </div>
            <ol className='border-t border-[#34243b]/20 dark:border-white/10'>
              {buildSteps.map((step) => (
                <li
                  key={step.number}
                  className='grid gap-3 border-b border-[#34243b]/20 py-7 dark:border-white/10 sm:grid-cols-[72px_0.75fr_1.25fr] sm:gap-6'
                >
                  <span className='font-mono text-sm font-bold text-[#7b6390] dark:text-[#bda7ef]'>
                    {step.number}
                  </span>
                  <h3 className='text-xl font-black'>{tr(step.title)}</h3>
                  <p className='leading-7 text-[#665a69] dark:text-slate-300'>
                    {tr(step.description)}
                  </p>
                </li>
              ))}
            </ol>
          </div>
        </div>
      </section>

      <section className='border-y border-[#34243b]/10 bg-[#ebe2d7] px-4 py-16 dark:border-white/10 dark:bg-slate-900 sm:px-6 lg:px-8'>
        <div className='mx-auto grid max-w-7xl gap-10 lg:grid-cols-3'>
          <div>
            <p className='text-sm font-black uppercase tracking-[0.2em] text-[#6d45c2] dark:text-[#bda7ef]'>
              {tr('Clear boundaries')}
            </p>
            <h2 className='mt-4 text-3xl font-black tracking-[-0.03em]'>
              {tr('What to know before you ship')}
            </h2>
          </div>
          <div className='lg:col-span-2'>
            <ul className='grid gap-5 md:grid-cols-2'>
              {[
                'Google Cloud Run is the built-in deployment target. AWS is possible by adapting the GitHub codebase or integrating AWS services with credentials.',
                'Payments, email, maps, calendars and other third-party services require suitable APIs, your credentials and real integration testing.',
                'Marky can implement metadata, structured data, sitemaps and internal links when requested; useful source content still determines search quality.',
                'Apps handling regulated or highly sensitive data need an independent security, privacy and compliance review before production use.',
              ].map((item) => (
                <li
                  key={item}
                  className='flex items-start gap-3 leading-7 text-[#514554] dark:text-slate-300'
                >
                  <CheckCircleIcon
                    className='mt-1 h-5 w-5 shrink-0 text-[#5a7c1b] dark:text-[#a9c95e]'
                    aria-hidden='true'
                  />
                  <span>{tr(item)}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      <section
        aria-labelledby='marky-website-faq'
        className='px-4 py-20 sm:px-6 lg:px-8 lg:py-28'
      >
        <div className='mx-auto grid max-w-7xl gap-12 lg:grid-cols-[0.7fr_1.3fr] lg:gap-20'>
          <div>
            <p className='text-sm font-black uppercase tracking-[0.2em] text-[#6d45c2] dark:text-[#bda7ef]'>
              {tr('Questions, answered')}
            </p>
            <h2
              id='marky-website-faq'
              className='mt-4 text-4xl font-black tracking-[-0.04em] sm:text-5xl'
            >
              {tr('Marky website builder FAQ')}
            </h2>
          </div>
          <div className='divide-y divide-[#34243b]/20 border-y border-[#34243b]/20 dark:divide-white/10 dark:border-white/10'>
            {localizedFaqs.map((faq) => (
              <details key={faq.question} className='group py-6'>
                <summary className='flex min-h-[44px] cursor-pointer list-none items-start justify-between gap-6 text-lg font-black focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-[#6d45c2] [&::-webkit-details-marker]:hidden'>
                  {faq.question}
                  <span
                    aria-hidden='true'
                    className='text-2xl font-normal text-[#6d45c2] group-open:rotate-45 dark:text-[#bda7ef]'
                  >
                    +
                  </span>
                </summary>
                <p
                  className={`max-w-3xl pb-2 pt-3 leading-7 text-[#665a69] dark:text-slate-300 ${
                    isRtl ? 'pl-10' : 'pr-10'
                  }`}
                >
                  {faq.answer}
                </p>
              </details>
            ))}
          </div>
        </div>
      </section>

      <section className='px-4 pb-20 sm:px-6 lg:px-8 lg:pb-28'>
        <div className='mx-auto flex max-w-7xl flex-col items-start justify-between gap-8 rounded-[2rem] bg-[#7047c5] px-7 py-10 text-[#fffaf3] sm:px-10 sm:py-12 lg:flex-row lg:items-end lg:px-14'>
          <div className='max-w-3xl'>
            <p className='text-sm font-black uppercase tracking-[0.2em] text-[#e2d7ff]'>
              {tr('Start with a useful brief')}
            </p>
            <h2 className='mt-3 text-3xl font-black tracking-[-0.035em] sm:text-4xl'>
              {tr(
                'Tell Marky who the website is for and what they need to accomplish.',
              )}
            </h2>
          </div>
          <Link
            href='/chat?mode=build-website'
            className='inline-flex min-h-[48px] shrink-0 items-center justify-center gap-2 rounded-full bg-[#fffaf3] px-6 py-3 font-bold text-[#34203f] transition-colors hover:bg-[#e9defa] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-white'
          >
            {tr('Start building')}
            <ArrowRightIcon
              className={`h-4 w-4 ${isRtl ? 'rotate-180' : ''}`}
              aria-hidden='true'
            />
          </Link>
        </div>
      </section>

      <Footer />
    </main>
  );
};

export default MarkyWebsiteHub;
